import './scrollTrigger';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import carousel from './carousel';
import { getRTL } from './_utils';

const cardsClassName = 'ExperienceCards';
const classes = {
	flickity: `${cardsClassName}--flickity`,
	cardItem: `${cardsClassName}-item`,
	textPanel: `${cardsClassName}-textPanel`,
	pagination: `${cardsClassName}-Pagination`,
	image: 'Image-img',
};

const selectors = {
	cardsContainer: `.${cardsClassName}`,
	cardItem: `.${classes.cardItem}`,
	textPanel: `.${classes.textPanel}`,
	pagination: `.${classes.pagination}`,
	image: `.${classes.image}`,
	cardsNextButton: `.${cardsClassName}-nextButton`,
	cardsPrevButton: `.${cardsClassName}-prevButton`,
	nav: 'nav',
	footer: '.Navigation-sticky-footer',
};
const isRTL = getRTL();

// left: 37, up: 38, right: 39, down: 40,
// spacebar: 32, pageup: 33, pagedown: 34, end: 35, home: 36, tab: 9
const keys = { 37: 1, 38: 1, 39: -1, 40: -1, 32: -1, 33: 1, 34: -1, 35: -1, 36: 1, 9: 0 };
let eventHandlers = {};

let ts;
window.addEventListener('touchstart', e => {
	ts = e.touches[0].clientY;
});

const getDirection = e => {
	let dir = keys[e.keyCode];
	if (e && e.changedTouches && e.changedTouches.length > 0) {
		dir = ts > e.changedTouches[0].clientY ? -1 : 1;
	} else if (dir === undefined || dir === null) {
		dir =
			e.wheelDelta ||
			-e.detail ||
			(e.originalEvent
				? e.originalEvent.wheelDelta ||
				  -e.originalEvent.detail ||
				  -(e.originalEvent.deltaY * 25)
				: e.wheelDelta);
	} else if (e.keyCode === 32) {
		//if focused on an element spacebar isn't for scrolling
		dir = document.activeElement.tagName.toLowerCase() !== 'body' ? undefined : dir;
	}
	return dir;
};

// modern Chrome requires { passive: false } when adding event
let supportsPassive = false;
try {
	window.addEventListener(
		'test',
		null,
		Object.defineProperty({}, 'passive', {
			get: function () {
				supportsPassive = true;
			},
		})
	);
} catch (e) {
	//empty by design
}

const wheelOpt = supportsPassive ? { passive: false, capture: true } : false;
const wheelEvent = 'onwheel' in document.createElement('div') ? 'wheel' : 'mousewheel';
const tops = {};
// call this to Disable scroll key/wheel event
function addScrollEvent(eventHandler) {
	window.addEventListener('DOMMouseScroll', eventHandler, false); // older FF
	window.addEventListener(wheelEvent, eventHandler, wheelOpt); // modern desktop
	window.addEventListener('touchmove', eventHandler, wheelOpt); // mobile
	window.addEventListener('keydown', eventHandler, false);
	window.addEventListener('keyup', eventHandler, false);
}

// call this to Enable scrolling on key/wheel
function removeScrollEvent(eventHandler) {
	window.removeEventListener('DOMMouseScroll', eventHandler, false);
	window.removeEventListener(wheelEvent, eventHandler, wheelOpt);
	window.removeEventListener('touchmove', eventHandler, wheelOpt);
	window.removeEventListener('keydown', eventHandler, false);
	window.removeEventListener('keyup', eventHandler, false);
}

// catch the initial scroll in
addScrollEvent(e => {
	const dir = getDirection(e);
	if (dir === undefined || dir === null) {
		return;
	}
	ScrollTrigger.getAll().forEach(st => {
		if (st && st.vars.onToggle && !st.isActive) {
			const target = st.scroll() - dir;
			if (target >= st.start && target < st.end) {
				st.vars.onToggle(st, true);
				try {
					eventHandlers[st.animation.vars.id.split('-').pop()]({ wheelDelta: dir });
				} catch (e) {
					//empty by design
				}
			}
		}
	});
});

const getNavHeight = () => {
	const nav = document.querySelector(selectors.nav);
	return nav ? nav.offsetHeight : 0;
};
const getFooterHeight = () => {
	const footer = document.querySelector(selectors.footer);
	return footer ? footer.offsetHeight : 0;
};
export default function experienceCards() {
	const timelines = [];
	let inlineCarouselSlides = [];
	ScrollTrigger.saveStyles(
		`${selectors.cardItem},${selectors.textPanel},${selectors.pagination},${selectors.image}`
	);
	ScrollTrigger.matchMedia({
		// large
		'(min-width: 961px)': function () {
			inlineCarouselSlides = inlineCarouselSlides.filter(
				({ $carouselContainer, flickity }) => {
					if ($carouselContainer.hasClass(cardsClassName)) {
						$carouselContainer.removeClass(classes.flickity);
						flickity.destroy();
					}
					return false;
				}
			);
			document
				.querySelectorAll(selectors.cardsContainer)
				.forEach((cardsContainer, containerIndex) => {
					const cards = cardsContainer.querySelectorAll(selectors.cardItem);

					const fixCardsHeight = () => {
						let refresh = false;
						const offset = getNavHeight() + getFooterHeight();
						const newHeight = `calc(100vh - ${offset}px)`;
						cards.forEach(card => {
							// correct card height
							let height = card.style.height;
							if (height && height.replace) {
								height = height.replace(/\s/g, '');
								if (!height.includes(`${offset}px`)) {
									refresh = true;
								}
							} else {
								refresh = true;
							}
							if (refresh) {
								card.style.height = newHeight;
							}
						});

						return refresh;
					};

					const onUpdate = () => {
						const increment = 1 / cards.length;
						const cardsST =
							ScrollTrigger.getById(`cards-${containerIndex}`) ||
							ScrollTrigger.getById(`cardScrollIn-${containerIndex}`);
						// if scrolled into via some scroll that we didn't catch (like manually dragging scroll bar) we want to lock them to the cards still
						if (
							cardsST &&
							cardsST.isActive &&
							cardsST.progress >= increment &&
							eventHandlers[containerIndex]
						) {
							eventHandlers[containerIndex]({ wheelDelta: 0, type: 'keyup' });
						}
						let refresh = fixCardsHeight();
						if (cardsST && !refresh) {
							const currTop = cardsST.trigger.getBoundingClientRect().top + scrollY;
							const prevTop = tops[`cards-${containerIndex}`] || currTop;
							if (Math.abs(currTop - prevTop) > 1) {
								refresh = true;
							}
							tops[`cards-${containerIndex}`] = currTop;
						}
						if (refresh) {
							ScrollTrigger.refresh(true);
						}
						//fix for pagination not being a fixed position and scrolltrigger issues with fading in/out
						let highestOpacity = 0;
						cards.forEach((card, i) => {
							const paginationElem = card.querySelector(selectors.pagination);
							if (
								card.style.transform.includes('scale(1') ||
								!card.style.transform.includes('scale')
							) {
								card.style.transform = '';
								const offset = '';
								const css = {
									top: `calc(50% + ${
										getNavHeight() / 2 - getFooterHeight() / 2
									}px)`,
								};
								if (isRTL) {
									css.left = offset;
								} else {
									css.right = offset;
								}
								$(paginationElem).css(css);
							} else {
								$(paginationElem).css({
									top: '',
									right: '',
								});
							}

							//fix pagination positioning and opacity
							if (paginationElem) {
								const lastCardST = ScrollTrigger.getById(
									`lastCard-${containerIndex}`
								);
								let opacity = 0;
								if (cardsST && cardsST.isActive) {
									const progressAtCardBot =
										cardsST.animation.labels[`end-${i}`] /
										cardsST.animation.totalDuration();
									opacity =
										1 -
										Math.abs(progressAtCardBot - cardsST.progress) / increment;
								} else if (i === cards.length - 1) {
									if (lastCardST && lastCardST.isActive) {
										opacity = 1 - lastCardST.progress;
									} else if (
										cardsST &&
										(Math.abs(cardsST.end - scrollY) < 1 ||
											Math.abs(lastCardST.end - scrollY) < 1) //at or very close to the end
									) {
										opacity = 1;
									}
								}
								if (opacity < 0.1) {
									paginationElem.style.display = 'none';
								} else {
									paginationElem.style.opacity = opacity;
									paginationElem.style.display = '';
									if (opacity > highestOpacity) {
										highestOpacity = opacity;
									}
								}
							}
						});
					};
					const onRefresh = () => {
						const cardsST =
							ScrollTrigger.getById(`cards-${containerIndex}`) ||
							ScrollTrigger.getById(`cardScrollIn-${containerIndex}`);
						if (cardsST && cardsST.isActive && eventHandlers[containerIndex]) {
							eventHandlers[containerIndex]({ wheelDelta: 0, type: 'keyup' });
						}
						onUpdate();
					};
					// make sure things are recomputed at resize
					window.addEventListener('resize', () => {
						onRefresh();
					});
					let tlCards;
					// setup timelines
					cards.forEach((card, index) => {
						if (index === 0) {
							const tl = gsap.timeline({
								id: `cardScrollIn-${containerIndex}`,
								scrollTrigger: {
									trigger: card,
									start: '40% bottom',
									end: () => {
										return `top top+=${getNavHeight()}px`;
									},
									scrub: true,
									onUpdate,
									onRefresh,
								},
							});
							timelines.push(tl);
							tl.addLabel('start')
								.from(
									card,
									{ duration: 3, scale: 0.66, transformOrigin: '50% 0' },
									'start'
								)
								.from(
									card.querySelector(selectors.textPanel),
									{
										duration: 3,
										opacity: 0,
										scale: 1.66,
										left: '20%',
										position: 'relative',
									},
									'start'
								)
								.addLabel('end');
						}

						if (cards.length > 1) {
							if (!tlCards) {
								tlCards = gsap.timeline({
									id: `cards-${containerIndex}`,
									defaults: { duration: 3 },
								});
								timelines.push(tlCards);
							}

							tlCards
								.addLabel(`start-${index}`)
								.from(
									card.querySelector(selectors.image),
									{ scale: 1.1 },
									`start-${index}`
								);
							if (index > 0) {
								const prevCard = cards[index - 1];
								tlCards.to(
									prevCard.querySelector(selectors.image),
									{ scale: 1.1 },
									`start-${index}`
								);
							}
							tlCards.addLabel(`end-${index}`);
						}

						if (index + 1 === cards.length) {
							if (tlCards) {
								//create scroll trigger for cards animating
								ScrollTrigger.create({
									animation: tlCards,
									trigger: cardsContainer,
									start: () => {
										return `top bottom-=${getFooterHeight()}px`;
									},
									end: () => {
										return `bottom bottom-=${getFooterHeight()}px`;
									},
									scrub: true,
									onUpdate,
									onRefresh,
								});
							}
							// add snap for last card
							const tlPagination = gsap.timeline({
								id: `lastCard-${containerIndex}`,
								duration: 3,
								scrollTrigger: {
									trigger: card,
									start: () => {
										return `top top+=${getNavHeight()}px`;
									},
									end: () => {
										return `40% top+=${getNavHeight()}px`;
									},
									scrub: true,
									onUpdate,
									onRefresh,
								},
							});
							timelines.push(tlPagination);
							//scroll out animation
							const tlEnd = gsap.timeline({
								id: `cardScrollOut-${containerIndex}`,
								scrollTrigger: {
									trigger: card,
									start: () => {
										return `40% top+=${getNavHeight()}px`;
									},
									end: () => {
										return `bottom top+=${getNavHeight()}px`;
									},
									scrub: true,
									onUpdate,
									onRefresh,
								},
							});
							timelines.push(tlEnd);
							tlEnd
								.addLabel('start')
								.to(
									card,
									{
										duration: 3,
										scale: 0.66,
										transformOrigin: '50% 0',
									},
									'start'
								)
								.to(
									card.querySelector(selectors.textPanel),
									{
										duration: 3,
										opacity: 0,
										scale: 1.66,
										left: '20%',
										position: 'relative',
									},
									'start'
								)
								.addLabel('end');
						}
					});
					fixCardsHeight();
				});
		},
		'(max-width: 960px)': function () {
			document
				.querySelectorAll(selectors.cardsContainer)
				.forEach((_cardContainer, containerIndex) => {
					if (eventHandlers[containerIndex]) {
						removeScrollEvent(eventHandlers[containerIndex]);
						eventHandlers[containerIndex] = null;
					}
				});
			inlineCarouselSlides = carousel(cardsClassName, [
				'xxsmall',
				'xsmall',
				'small',
				'medium',
			]);
			inlineCarouselSlides.forEach(({ $carouselContainer, flickity }) => {
				flickity.on('change', function (i) {
					const index = i + 1;
					const paginationVal = $carouselContainer
						.find(`${selectors.pagination}[data-index="${index}"] > span`)
						.text();
					const pagination = $carouselContainer.children(selectors.pagination);
					pagination.children('span').text(paginationVal);
					const nextButton = pagination.children(selectors.cardsNextButton);
					const prevButton = pagination.children(selectors.cardsPrevButton);
					if (flickity.slides.length === index) {
						nextButton.attr('disabled', true);
						nextButton.attr('aria-disabled', 'true');
					} else {
						nextButton.attr('disabled', false);
						nextButton.attr('aria-disabled', 'false');
					}
					if (index === 1) {
						prevButton.attr('disabled', true);
						prevButton.attr('aria-disabled', 'true');
					} else {
						prevButton.attr('disabled', false);
						prevButton.attr('aria-disabled', 'false');
					}
				});
			});
		},
	});
}
